import { Dayjs } from 'dayjs'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Shift } from '../../types/schedule.model'
import { RangeSelection, useShiftDrag } from '../../hooks/useShiftDrag'
import OverlayScrollbar from '@/shared/components/OverlayScrollbar/OverlayScrollbar'
import { tileBackground } from '../../utils/colors'
import {
  DAY_GRID_END_HOUR,
  DAY_GRID_START_HOUR,
  DEFAULT_VISIBLE_HOUR,
  DRAG_STEP_MINUTES,
  MINUTES_IN_DAY,
  MINUTES_IN_HOUR,
  crossesMidnight,
  dayjs,
  formatMinutes,
  positionOverlappingShifts,
  toDateKey
} from '../../utils/schedule'
import styles from './WeekCalendar.module.scss'
import {
  DEFAULT_SHIFT_TYPE,
  ShiftType,
  isAllDayType,
  shiftTypeDefinition
} from '../../types/shiftType.model'

/*
  The type printed on a block, or nothing.

  Working time is the default on nearly every block, so naming it there is noise
  rather than information, and an all-day entry already carries its name where
  the times would otherwise be.
*/
const blockTypeLabel = (
  type: ShiftType | undefined,
  translate: (key: string) => string
): string | null => {
  const definition = shiftTypeDefinition(type)
  if (definition.allDay || definition.type === DEFAULT_SHIFT_TYPE) return null
  return translate(definition.labelKey)
}

interface WeekCalendarProps {
  days: Dayjs[]
  shiftsForDay: (day: Dayjs) => Shift[]
  employeeName: (userId: string) => string
  employeeColor: (userId: string) => string
  onEditShift: (shift: Shift) => void
  /** A swept-out (or clicked) empty period starts a new shift */
  onAddShift: (date: string, startMinutes: number, endMinutes?: number) => void
  /** Dropping a dragged shift commits its new day and times */
  onMoveShift: (shift: Shift) => void
  isLoading?: boolean
}

/**
 * How many shifts share the column width before they start stacking instead of
 * dividing it further. Splitting seven ways makes every block an unreadable
 * sliver; three is the point where a name still fits.
 */
const MAX_SIDE_BY_SIDE = 3

const isToday = (day: Dayjs) => day.isSame(dayjs(), 'day')

function WeekCalendar(props: WeekCalendarProps) {
  const {
    days,
    shiftsForDay,
    employeeName,
    employeeColor,
    onEditShift,
    onAddShift,
    onMoveShift,
    isLoading
  } = props
  const { t, i18n } = useTranslation()

  const hours = useMemo(
    () =>
      Array.from(
        { length: DAY_GRID_END_HOUR - DAY_GRID_START_HOUR },
        (_, index) => DAY_GRID_START_HOUR + index
      ),
    []
  )

  /**
   * One entry per half hour, because that is the granularity a drag snaps to.
   * Splitting the row also means the hover highlight matches the slot you will
   * actually get, instead of lighting up a whole hour for a 30-minute target.
   */
  const slots = useMemo(
    () =>
      hours.flatMap(hour => [
        { hour, minute: 0 },
        { hour, minute: DRAG_STEP_MINUTES }
      ]),
    [hours]
  )

  const gridStartMinutes = DAY_GRID_START_HOUR * MINUTES_IN_HOUR
  const gridEndMinutes = DAY_GRID_END_HOUR * MINUTES_IN_HOUR
  // Geometry is a share of the visible window, not a fixed px/rem per hour, so
  // the calendar fills whatever height the card has instead of overflowing it.
  const windowMinutes = gridEndMinutes - gridStartMinutes

  const bodyRef = useRef<HTMLDivElement | null>(null)
  /**
   * The placeholder calendar's own scroll container.
   *
   * Deliberately not `bodyRef`: the scroll-to-first-shift effect below records
   * the week it has handled, so pointing that ref at the skeleton would mark
   * the week as already scrolled and the real calendar would open at midnight
   * when the data landed.
   */
  const skeletonBodyRef = useRef<HTMLDivElement | null>(null)
  const columnRefs = useRef(new Map<string, HTMLDivElement>())

  const registerColumn = useCallback(
    (dateKey: string) => (element: HTMLDivElement | null) => {
      if (element) {
        columnRefs.current.set(dateKey, element)
      } else {
        columnRefs.current.delete(dateKey)
      }
    },
    []
  )

  const resolveDateAtX = useCallback((clientX: number): string | null => {
    let found: string | null = null
    columnRefs.current.forEach((element, dateKey) => {
      const rect = element.getBoundingClientRect()
      if (clientX >= rect.left && clientX < rect.right) found = dateKey
    })
    return found
  }, [])

  // Measured live on every pointer move, against the scrolled content box -
  // a frozen ratio would drift the moment the body scrolls or the rows
  // restretch mid-drag.
  const resolveMinutesAtY = useCallback(
    (clientY: number): number | null => {
      const element = bodyRef.current
      if (!element) return null

      const contentHeight = element.scrollHeight
      if (!contentHeight) return null

      const contentTop = element.getBoundingClientRect().top - element.scrollTop
      const ratio = (clientY - contentTop) / contentHeight

      return gridStartMinutes + ratio * windowMinutes
    },
    [gridStartMinutes, windowMinutes]
  )

  const handleCommitRange = useCallback(
    (selection: RangeSelection) =>
      onAddShift(selection.date, selection.startMinutes, selection.endMinutes),
    [onAddShift]
  )

  const { preview, range, beginShiftDrag, beginRangeSelect, isClickFromDrag } =
    useShiftDrag({
      gridStartMinutes,
      gridEndMinutes,
      resolveDateAtX,
      resolveMinutesAtY,
      onCommitShift: onMoveShift,
      onCommitRange: handleCommitRange
    })

  // While dragging, the column renders the preview instead of the stored shift,
  // including in a different column once the pointer crosses into one. The
  // preview replaces the shift in place so side-by-side order doesn't shuffle.
  const shiftsForColumn = useCallback(
    (day: Dayjs): Shift[] => {
      const dateKey = toDateKey(day)
      const stored = shiftsForDay(day)

      if (!preview) return stored

      if (preview.date === dateKey) {
        return stored.some(shift => shift.id === preview.id)
          ? stored.map(shift => (shift.id === preview.id ? preview : shift))
          : [...stored, preview]
      }

      return stored.filter(shift => shift.id !== preview.id)
    },
    [shiftsForDay, preview]
  )

  const toPercent = useCallback(
    (minutes: number) => ((minutes - gridStartMinutes) / windowMinutes) * 100,
    [gridStartMinutes, windowMinutes]
  )

  // A full 24h day almost never fits, so the week opens at the hour the day
  // starts being interesting rather than on empty night. Runs once per week -
  // re-running on every render would fight the user's own scrolling.
  const scrolledWeekRef = useRef<string | null>(null)

  useEffect(() => {
    const element = bodyRef.current
    const weekKey = days.length ? toDateKey(days[0]) : null
    if (!element || !weekKey || scrolledWeekRef.current === weekKey) return

    scrolledWeekRef.current = weekKey
    element.scrollTop =
      ((DEFAULT_VISIBLE_HOUR * MINUTES_IN_HOUR) / windowMinutes) *
      element.scrollHeight
    // `isLoading` is a dependency because `bodyRef` is only attached in the
    // loaded branch: while the placeholder is up there is nothing to scroll,
    // and without this the effect would never run again once it arrived.
  }, [days, windowMinutes, isLoading])

  // The real calendar minus its shifts: headers, hour gutter, column geometry
  // and the scrollbar are all the loaded ones, so nothing moves when the data
  // arrives. No placeholder blocks, invented grey shifts read as real ones
  // being selected, and they flashed on every step to an uncached week.
  if (isLoading) {
    return (
      <div className={styles.calendar}>
        <div className={styles.headerRow}>
          <div className={styles.gutterCorner} />
          {days.map(day => (
            <div key={toDateKey(day)} className={styles.dayHeader}>
              <span className="body-bold-medium">
                {day.locale(i18n.language).format('ddd')}
              </span>
              <span className={`body-small ${styles.dayHeaderDate}`}>
                {day.format('D.MM')}
              </span>
            </div>
          ))}
        </div>

        <div className={styles.bodyWrapper}>
          {/*
            The native bar is hidden in CSS and this one is drawn over the
            content, so leaving it out of the loading branch is not a missing
            decoration, it is the only scrollbar there is, and it vanished on
            every step to a week that was not cached yet.
          */}
          <OverlayScrollbar targetRef={skeletonBodyRef} />
          <div className={styles.body} ref={skeletonBodyRef}>
            <div className={styles.hourGutter}>
              {hours.map(hour => (
                <div key={hour} className={`body-small ${styles.hourLabel}`}>
                  {`${String(hour).padStart(2, '0')}:00`}
                </div>
              ))}
            </div>

            {days.map(day => (
              <div key={toDateKey(day)} className={styles.dayColumn}>
                {hours.map(hour => (
                  <div key={hour} className={styles.hourSlotStatic} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.calendar}>
      <div className={styles.headerRow}>
        <div className={styles.gutterCorner} />
        {days.map(day => (
          <div
            key={toDateKey(day)}
            className={`${styles.dayHeader} ${isToday(day) ? styles.today : ''}`}
          >
            <span className="body-bold-medium">
              {day.locale(i18n.language).format('ddd')}
            </span>
            <span className={`body-small ${styles.dayHeaderDate}`}>
              {day.format('D.MM')}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.bodyWrapper}>
        <OverlayScrollbar targetRef={bodyRef} />
        <div className={styles.body} ref={bodyRef}>
          <div className={styles.hourGutter}>
            {hours.map(hour => (
              <div key={hour} className={`body-small ${styles.hourLabel}`}>
                {`${String(hour).padStart(2, '0')}:00`}
              </div>
            ))}
          </div>

          {days.map((day, dayIndex) => {
            const dateKey = toDateKey(day)
            const positioned = positionOverlappingShifts(shiftsForColumn(day))

            return (
              <div
                key={dateKey}
                ref={registerColumn(dateKey)}
                className={`${styles.dayColumn} ${
                  isToday(day) ? styles.todayColumn : ''
                }`}
              >
                {slots.map(({ hour, minute }) => (
                  <button
                    type="button"
                    key={`${hour}-${minute}`}
                    className={`${styles.slot} ${
                      minute ? styles.slotHalf : ''
                    }`}
                    onPointerDown={event => beginRangeSelect(event, dateKey)}
                    onClick={() => {
                      if (isClickFromDrag()) return
                      onAddShift(dateKey, hour * MINUTES_IN_HOUR + minute)
                    }}
                    aria-label={`${t('SCHEDULE.ADD_SHIFT')} - ${day.format(
                      'DD.MM'
                    )} ${String(hour).padStart(2, '0')}:${String(
                      minute
                    ).padStart(2, '0')}`}
                  />
                ))}

                {range && range.date === dateKey && (
                  <div
                    className={styles.rangePreview}
                    style={{
                      top: `${toPercent(range.startMinutes)}%`,
                      height: `${
                        toPercent(range.endMinutes) -
                        toPercent(range.startMinutes)
                      }%`
                    }}
                  >
                    <span className="body-small">
                      {`${formatMinutes(range.startMinutes)}–${formatMinutes(
                        range.endMinutes
                      )}`}
                    </span>
                  </div>
                )}

                {/*
                The tail of a shift that started the previous day. Read-only -
                it is the same shift, so it is edited from its own block, but
                without it a 22:00-04:00 shift looks like it ends at midnight.
              */}
                {(() => {
                  const previous = days[dayIndex - 1]
                  if (!previous) return null

                  return shiftsForDay(previous)
                    .filter(shift => shift.endMinutes > MINUTES_IN_DAY)
                    .map(shift => {
                      const end = Math.min(
                        shift.endMinutes - MINUTES_IN_DAY,
                        gridEndMinutes
                      )
                      const color = employeeColor(shift.userId)

                      return (
                        <div
                          key={`${shift.id}-tail`}
                          className={`${styles.shiftBlock} ${styles.shiftTail}`}
                          role="button"
                          tabIndex={0}
                          title={shift.note}
                          onClick={() => onEditShift(shift)}
                          onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              onEditShift(shift)
                            }
                          }}
                          style={{
                            top: 0,
                            height: `calc(${toPercent(end)}% - 2px)`,
                            left: '0.125rem',
                            width: 'calc(100% - 0.25rem)',
                            borderLeftColor: color,
                            background: tileBackground(color)
                          }}
                        >
                          <span
                            className={`body-bold-small ${styles.shiftName}`}
                          >
                            {employeeName(shift.userId)}
                          </span>
                          <span className={`body-small ${styles.shiftTime}`}>
                            {isAllDayType(shift.type)
                              ? t(shiftTypeDefinition(shift.type).labelKey)
                              : `${formatMinutes(shift.startMinutes)}–${formatMinutes(
                                  shift.endMinutes
                                )}`}
                          </span>
                          {blockTypeLabel(shift.type, t) && (
                            <span className={`body-small ${styles.shiftType}`}>
                              {blockTypeLabel(shift.type, t)}
                            </span>
                          )}
                          {shift.note && (
                            <span className={`body-small ${styles.shiftNote}`}>
                              {shift.note}
                            </span>
                          )}
                        </div>
                      )
                    })
                })()}

                {positioned
                  // A shift lying wholly outside the visible window would clamp
                  // to a negative height, which the CSSOM drops, leaving a
                  // shrink-wrapped phantom block on top of a real hour slot.
                  .filter(
                    ({ shift }) =>
                      shift.endMinutes > gridStartMinutes &&
                      shift.startMinutes < gridEndMinutes
                  )
                  .map(({ shift, column, columns }) => {
                    const start = Math.max(shift.startMinutes, gridStartMinutes)
                    const end = Math.max(
                      start,
                      Math.min(shift.endMinutes, gridEndMinutes)
                    )
                    const isDragged = preview?.id === shift.id
                    const color = employeeColor(shift.userId)

                    // Past three abreast the blocks stack with a small indent
                    // instead of getting thinner and thinner.
                    const lanes = Math.min(columns, MAX_SIDE_BY_SIDE)
                    const lane = column % lanes
                    const depth = Math.floor(column / lanes)
                    const slot = 100 / lanes
                    const indent = 0.125 + depth * 0.5

                    return (
                      <div
                        key={shift.id}
                        className={`${styles.shiftBlock} ${
                          isDragged ? styles.dragging : ''
                        }`}
                        role="button"
                        tabIndex={0}
                        title={shift.note}
                        onPointerDown={event =>
                          beginShiftDrag(event, shift, 'move')
                        }
                        onClick={() => {
                          if (isClickFromDrag()) return
                          onEditShift(shift)
                        }}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            onEditShift(shift)
                          }
                        }}
                        style={{
                          top: `${toPercent(start)}%`,
                          // 2px shaved off the bottom: back-to-back shifts share
                          // an edge and would otherwise read as one long block.
                          height: `calc(${
                            toPercent(end) - toPercent(start)
                          }% - 2px)`,
                          left: `calc(${lane * slot}% + ${indent}rem)`,
                          width: `calc(${slot}% - ${indent + 0.125}rem)`,
                          zIndex: depth + 1,
                          borderLeftColor: color,
                          background: tileBackground(color)
                        }}
                      >
                        <span
                          className={styles.resizeHandle}
                          role="presentation"
                          onPointerDown={event =>
                            beginShiftDrag(event, shift, 'resize-start')
                          }
                        />

                        <span className={`body-bold-small ${styles.shiftName}`}>
                          {employeeName(shift.userId)}
                        </span>
                        <span className={`body-small ${styles.shiftTime}`}>
                          {isAllDayType(shift.type)
                            ? t(shiftTypeDefinition(shift.type).labelKey)
                            : `${formatMinutes(shift.startMinutes)}–${formatMinutes(
                                shift.endMinutes
                              )}`}
                        </span>
                        {blockTypeLabel(shift.type, t) && (
                          <span className={`body-small ${styles.shiftType}`}>
                            {blockTypeLabel(shift.type, t)}
                          </span>
                        )}
                        {shift.note && (
                          <span className={`body-small ${styles.shiftNote}`}>
                            {shift.note}
                          </span>
                        )}

                        {/*
                          No end handle on an overnight shift: the block's
                          bottom edge is midnight, not the shift's real end, so
                          dragging it would clamp 22:00-02:00 down to
                          22:00-24:00 and drop two hours without saying so.
                          Those ends are edited in the modal.
                        */}
                        {!crossesMidnight(shift) && (
                          <span
                            className={`${styles.resizeHandle} ${styles.resizeHandleEnd}`}
                            role="presentation"
                            onPointerDown={event =>
                              beginShiftDrag(event, shift, 'resize-end')
                            }
                          />
                        )}
                      </div>
                    )
                  })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default WeekCalendar
